import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import callApi from 'utils/api';
import { ticketWorklistId, urlSigns } from '../../../utils/app-configs';
import { generatePreviewId } from '../../../utils/helpers';
import { getWorklistDataByIdRequest } from '../../../worklist/containers/worklist-actions';
import * as actionTypes from './constants';
import * as actions from './toolbar-dynamic-actions-actions';

export const getCalculateTravelTime = state => state.gantt.boardData.calculateTravelTime;
export const getShowTravelTime = state => state.planningBoard.showTravelTime;

/** Create draft orders */

export function* doCreateSingleDraftOrder(body) {
  try {
    const result = yield call(callApi, {
      endpoint: 'order/new',
      options: {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        errorMessage: `Could not create new draft order for ${body.scheduledId
          }`,
      },
    });
    return result.id;
  } catch (err) {
    yield put(actions.createSingleOrderFailure(err));
    return null;
  }
}

export function* doCreateDraftOrders({ payload }) {
  try {
    const { actions: payloadActions, newDate } = payload;
    const orderCalls = payloadActions?.map(a =>
      call(doCreateSingleDraftOrder, { ...a, newDate }),
    );
    const results = yield all(orderCalls);
    yield put(actions.createDraftOrdersSuccess(results?.filter(r => r)));
  } catch (err) {
    yield put(actions.createDraftOrdersFailure(err));
  }
}

/** Create auto orders (background processing) */
export function* doCreateAutoOrders({ payload }) {
  const {
    isDelete = false,
    workcenterId,
    actions: payloadActions,
    newDate,
    shouldUpdatePlanningboard,
    hasSequentialOrder,
    toStatus,
  } = payload;

  let id;
  if (shouldUpdatePlanningboard) {
    id = generatePreviewId({
      laneId: workcenterId,
      linkedEntityId: payloadActions?.map(e => e.id).join(urlSigns.keyValueSeparator),
    });
  }
  try {
    const calculateTravelTime = yield select(getCalculateTravelTime);
    const showTravelTime = yield select(getShowTravelTime);

    let body = {
      actions: payloadActions,
      newDate,
      newTechnicianId: workcenterId,
      movePlanningItemsToStatus: toStatus,
      startFromMainActivity: !calculateTravelTime && !showTravelTime,
    };

    if (hasSequentialOrder !== null) {
      body = {
        ...body,
        planItemsSequentially: hasSequentialOrder,
      };
    }

    Object.keys(body).forEach(key => (body[key] == null) && delete body[key]);

    const result = yield call(callApi, {
      endpoint: 'order/auto/multiple',
      options: {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        errorMessage: 'Could not process orders in the background',
      },
    });

    if (isDelete) {
      if (payload.worklistId) {
        const orderIds = payloadActions?.map(p => p.entityId);
        if (orderIds?.length) {
          yield put(getWorklistDataByIdRequest({
            ids: result.successful,
            worklistId: payload.worklistId,
            isDelete: true,
            highlightIds: [],
          }));
        }
      }
      yield put(actions.deleteOrdersSuccess({ ...result, actions: payloadActions }));
    } else {
      const results = shouldUpdatePlanningboard ?
        {
          ...result,
          shouldUpdatePlanningboard,
          workcenterIds: workcenterId ?
            [workcenterId] :
            result.planningBlocks?.map(pb => pb.laneId),
          id,
        } :
        result;

      if (payload.worklistId) {
        const orderIds = payloadActions?.map(p => p.entityId);
        if (orderIds?.length) {
          yield put(getWorklistDataByIdRequest({
            ids: results.successful,
            worklistId: payload.worklistId,
            highlightIds: [...result.successful, ...result.planningBlocks?.map(b => b.id)],
          }));
        }
      }
      yield put(actions.createAutoOrdersSuccess(results));
    }
  } catch (err) {
    if (isDelete) {
      yield put(actions.deleteOrdersFailure(err));
    } else {
      const error = shouldUpdatePlanningboard ?
        {
          ...err,
          shouldUpdatePlanningboard,
          workcenterId,
          id,
        } :
        err;
      yield put(actions.createAutoOrdersFailure(error));
    }
  }
}

export function* doAutoPlanningAction({ payload }) {
  const calculateTravelTime = yield select(getCalculateTravelTime);
  const showTravelTime = yield select(getShowTravelTime);

  const {
    actions: payloadActions,
    boardId,
    newDate,
    shouldUpdatePlanningboard,
    workcenterId,
    hasSequentialOrder,
  } = payload;
  let id;
  if (shouldUpdatePlanningboard && workcenterId) {
    id = generatePreviewId({
      laneId: workcenterId,
      linkedEntityId: payloadActions?.map(e => e.id).join(urlSigns.keyValueSeparator),
    });
  }

  try {
    let body = {
      actions: payloadActions,
      newDate,
      newLaneId: workcenterId,
      startFromMainActivity: !calculateTravelTime && !showTravelTime,
    };

    if (hasSequentialOrder !== null) {
      body = {
        ...body,
        planItemsSequentially: hasSequentialOrder,
      };
    }

    const result = yield call(callApi, {
      // eslint-disable-next-line max-len
      endpoint: `planning/${boardId}/multi`,
      options: {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        errorMessage: 'Could not process planning in the background',
      },
    });

    const results = shouldUpdatePlanningboard ?
      {
        ...result,
        shouldUpdatePlanningboard,
        workcenterId,
        id,
      } :
      result;
    if (payload.worklistId) {
      const orderIds = results?.successfulBlocks?.map(p => p.objectId);
      if (orderIds?.length) {
        yield put(getWorklistDataByIdRequest({
          ids: orderIds,
          worklistId: payload.worklistId,
          highlightIds: [...orderIds, ...results?.successfulBlocks?.map(p => p.id)],
        }));
      }
    }
    yield put(actions.autoPlanningActionSuccess(results));
  } catch (err) {
    const error = shouldUpdatePlanningboard ?
      {
        ...err,
        shouldUpdatePlanningboard,
        workcenterId,
        id,
      } :
      err;
    yield put(actions.autoPlanningActionFailure(error));
  }
}

export function* doCreateAutoTickets({ payload }) {
  const {
    actions: payloadActions,
  } = payload;

  try {
    const body = {
      actions: payloadActions,
    };

    Object.keys(body).forEach(key => (body[key] == null) && delete body[key]);

    const result = yield call(callApi, {
      endpoint: 'ticket/auto/multiple',
      options: {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        errorMessage: 'Could not process tickets in the background',
      },
    });

    if (result?.successful?.length) {
      yield put(getWorklistDataByIdRequest({
        ids: result.successful,
        worklistId: ticketWorklistId,
        highlightIds: result.successful,
      }));
    }

    yield put(actions.createAutoTicketsSuccess(result));
  } catch (err) {
    yield put(actions.createAutoTicketsFailure(err));
  }
}

function* watchCreateDraftOrders() {
  yield takeLatest(actionTypes.CREATE_DRAFT_ORDERS, doCreateDraftOrders);
}

function* watchCreateAutoOrders() {
  yield takeLatest(actionTypes.CREATE_AUTO_ORDERS, doCreateAutoOrders);
}

function* watchCreateAutoTickets() {
  yield takeLatest(actionTypes.CREATE_AUTO_TICKETS, doCreateAutoTickets);
}

function* watchDeleteOrders() {
  yield takeLatest(actionTypes.DELETE_ORDERS, doCreateAutoOrders);
}

function* watchAutoPlanningActions() {
  yield takeLatest(actionTypes.AUTO_PLANNING_ACTION, doAutoPlanningAction);
}

export default [
  fork(watchCreateDraftOrders),
  fork(watchCreateAutoOrders),
  fork(watchCreateAutoTickets),
  fork(watchDeleteOrders),
  fork(watchAutoPlanningActions),
];
